import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faBoxes,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faCalculator,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faUserPlus,
    faMinus,
    faArrowsAlt,
    faExpand,
    faCompress,
    faInfoCircle,
    faShoppingCart,
    faReceipt,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createStockListTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Stock List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        // Main headers
        stockList: 'Stock List',
        allStock: 'My Stock',
        inventory: 'Inventory',

        // Search and actions
        searchPlaceholder: 'Search stock items...',
        refresh: 'Refresh',
        loadingStock: 'Loading stock items...',
        noStockFound: 'No stock items found',

        // Modal texts
        stockDetails: 'Stock Details',
        close: 'Close',

        // Table headers
        serialNo: 'S.No',
        updatedAt: 'Updated',
        id: 'ID',
        productName: 'Product Name',
        batchNumber: 'Batch',
        barcode: 'Barcode',
        category: 'Category',
        company: 'Company',
        unit: 'Unit',
        quantity: 'Qty',
        stockValue: 'Stock Value',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        stockCategory: 'Type',

        // Stock category options
        saleable: 'Saleable',
        raw: 'Raw Material',

        // Action buttons
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',

        // Messages
        deleteConfirm: 'Are you sure you want to delete this stock item?',
        deletedSuccess: 'Stock item deleted successfully',
        deleteError: 'Error deleting stock item',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',

        // Modal details
        productId: 'Product ID',
        batchNo: 'Batch Number',
        name: 'Name',
        notProvided: 'Not provided',
        inStock: 'In Stock',
        lowStock: 'Low Stock',
        outOfStock: 'Out of Stock',

        // Pagination
        showing: 'Showing',
        of: 'of',
        items: 'items',
        previous: 'Previous',
        next: 'Next',

        // Filter texts
        filter: 'Filter',
        all: 'All',
        lowStockFilter: 'Low Stock',
        outOfStockFilter: 'Out of Stock',
        batchNumberFilter: 'Batch Number',
        barcodeFilter: 'Barcode',
        categoryFilter: 'Category',
        companyFilter: 'Company',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        resetFilters: 'Reset Filters',
        exportToPDF: 'PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        addedByFilter: 'Added By',

        // PDF Options
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        reportFilters: 'Applied Filters',

        // Edit Modal
        editStock: 'Edit Stock',
        updateStock: 'Update Stock',
        updating: 'Updating...',
        stockUpdated: 'Stock updated successfully',
        updateError: 'Error updating stock',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the stock details below',

        // Validation messages
        nameRequired: 'Product name is required',
        batchRequired: 'Batch number is required',
        unitRequired: 'Unit is required',
        quantityRequired: 'Quantity is required',
        invalidQuantity: 'Please enter a valid quantity',

        // Delete Confirmation Modal
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this stock item?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',

        // User-specific messages
        myRecords: 'My Records Only',
        loadingUserData: 'Loading your stock data...',
        noUserRecords: 'No stock found in this shop',

        // Business Info
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        stockReport: 'Stock Report',
        inventoryReport: 'Inventory Report',

        // Toast Messages
        success: 'Success',
        error: 'Error',
        warning: 'Warning',

        // Stock Status
        stockStatus: 'Stock Status',
        totalValue: 'Total Value',

        // Categories
        food: 'Food',
        other: 'Other',

        // Units
        piece: 'Piece',
        box: 'Box',
        packet: 'Packet',
        kg: 'Kilogram',
        gram: 'Gram',
        liter: 'Liter',
        ml: 'Milliliter',
        dabba: 'ڈبہ ',
        borai: 'بوری',
        dozen: 'Dozen',
        
        // Additional edit modal texts
        readonlyNotice: 'Batch number cannot be edited after creation',
        editMode: 'Edit Mode',
        optional: 'Optional',
        
        // Width modes
        narrow: 'Narrow',
        medium: 'Medium',
        wide: 'Wide',
        fullWidth: 'Full Width',
        fullScreen: 'Full Screen'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const stockListTranslator = createStockListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 400, label: 'narrow', icon: faMinus },
    medium: { width: 650, label: 'medium', icon: faArrowsAlt },
    wide: { width: 850, label: 'wide', icon: faPlus },
    full: { width: '95vw', label: 'fullWidth', icon: faExpand },
    fullscreen: { width: '98vw', label: 'fullScreen', icon: faCompress }
};

const StockList = () => {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [currentShop, setCurrentShop] = useState(null);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('portrait');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        product_name: '',
        batch_no: '',
        barcode: '',
        category: '',
        company: '',
        unit: '',
        quantity: '',
        quantity_alert: '',
        stock_category: 'Saleable'
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });
    const [businessInfoLoading, setBusinessInfoLoading] = useState(false);

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [batchFilter, setBatchFilter] = useState('');
    const [barcodeFilter, setBarcodeFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [stockCategoryFilter, setStockCategoryFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Stock category options for filter
    const stockCategoryOptions = [
        { value: '', label: 'All Types' },
        { value: 'Saleable', label: 'Saleable' },
        { value: 'Raw', label: 'Raw Material' }
    ];

    // Calculate rows per page based on orientation and page size
    const getRowsPerPage = () => {
        if (pdfPageSize === 'a5') return pdfOrientation === 'portrait' ? 28 : 32;
        if (pdfPageSize === 'legal') return pdfOrientation === 'portrait' ? 32 : 36;
        if (pdfPageSize === 'letter') return pdfOrientation === 'portrait' ? 30 : 34;
        return pdfOrientation === 'portrait' ? 28 : 32;
    };

    // Categories and units for dropdowns
    const categories = [
        { value: '', label: 'Select category' },
        { value: 'food', label: 'Food' },
        { value: 'other', label: 'Other' }
    ];

    const units = [
        { value: '', label: 'Select unit' },
        { value: 'piece', label: 'Piece' },
        { value: 'box', label: 'Box' },
        { value: 'packet', label: 'Packet' },
        { value: 'kg', label: 'Kilogram' },
        { value: 'gram', label: 'Gram' },
        { value: 'liter', label: 'Liter' },
        { value: 'ml', label: 'Milliliter' },
        { value: 'dabba', label: 'Dabba' },
        { value: 'burai', label: 'Burai' },
        { value: 'dozen', label: 'Dozen' }
    ];

    // Calculate totals
    const totalProducts = filteredProducts.length;
    const totalStockValue = filteredProducts.reduce((sum, product) => {
        const quantity = parseFloat(product.quantity) || 0;
        return sum + quantity;
    }, 0);
    const lowStockCount = filteredProducts.filter(product => {
        const quantity = parseFloat(product.quantity) || 0;
        const alert = parseFloat(product.quantity_alert) || 0;
        return quantity <= alert && quantity > 0;
    }).length;
    const outOfStockCount = filteredProducts.filter(product => {
        const quantity = parseFloat(product.quantity) || 0;
        return quantity <= 0;
    }).length;
    const saleableCount = filteredProducts.filter(product => product.stock_category === 'Saleable').length;
    const rawCount = filteredProducts.filter(product => product.stock_category === 'Raw').length;

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    // Format date time
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        } catch (error) {
            return dateString;
        }
    };

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDragging(true);
        
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!modalRef.current) return;
        
        setIsResizing(true);
        resizeType.current = type;
        
        const rect = modalRef.current.getBoundingClientRect();
        
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 350,
            minHeight: 450,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                
                switch (resizeType.current) {
                    case 'e':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        break;
                    case 'w':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'n':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'sw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                }
                
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        
        if (!showEditModal) {
            setIsModalInitialized(false);
        }
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await stockListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch products on mount - ONLY SALEABLE PRODUCTS
    useEffect(() => {
        if (user && user.username) {
            fetchProducts();
            fetchBusinessInfo();
            
            const shopId = localStorage.getItem('current_shop_id');
            const shopName = localStorage.getItem('current_shop_name');
            if (shopId) {
                setCurrentShop({ id: shopId, name: shopName });
            }
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [products, searchTerm, filterType, filterBy, batchFilter, barcodeFilter, categoryFilter, companyFilter, addedByFilter, stockCategoryFilter, startDate, endDate]);

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        setBusinessInfoLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || '',
                bussinesslogo: '',
                businessaddress: '',
                fullname: user.fullname || '',
                email: user.email || ''
            });
        } finally {
            setBusinessInfoLoading(false);
        }
    };

    const fetchProducts = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        const currentShopId = localStorage.getItem('current_shop_id') || user.shop_id;
        
        if (!currentShopId) {
            showToast('error', translations.noShopSelected || 'No shop selected. Please select a shop first.');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShopId,
                    filters: {
                        username: user.username
                    }
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                // Filter to show ONLY Saleable products (not Raw Materials)
                const saleableProducts = data.data.filter(product => 
                    product.stock_category === 'Saleable' || 
                    product.stock_category === 'saleable' ||
                    product.stock_category?.toLowerCase() === 'saleable' ||
                    !product.stock_category || // If no category, treat as saleable
                    (product.stock_category !== 'Raw' && product.stock_category !== 'raw' && product.stock_category?.toLowerCase() !== 'raw')
                );
                setProducts(saleableProducts);
                resetFilters();

                if (data.shop_name) {
                    showToast('info', `Showing stock from: ${data.shop_name}`);
                    setCurrentShop({ id: data.shop_id, name: data.shop_name });
                }

                if (saleableProducts.length === 0) {
                    showToast('info', translations.noUserRecords || 'No stock found in this shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...products];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.product_name?.toLowerCase().includes(term) ||
                product.batch_no?.toLowerCase().includes(term) ||
                product.barcode?.toLowerCase().includes(term) ||
                product.company?.toLowerCase().includes(term) ||
                product.category?.toLowerCase().includes(term) ||
                product.added_by_username?.toLowerCase().includes(term) ||
                product.username?.toLowerCase().includes(term)
            );
        }

        // Apply stock category filter
        if (stockCategoryFilter) {
            filtered = filtered.filter(product => product.stock_category === stockCategoryFilter);
        }

        if (filterType === 'lowStock') {
            filtered = filtered.filter(product => {
                const quantity = parseFloat(product.quantity) || 0;
                const alert = parseFloat(product.quantity_alert) || 0;
                return quantity <= alert && quantity > 0;
            });
        } else if (filterType === 'outOfStock') {
            filtered = filtered.filter(product => {
                const quantity = parseFloat(product.quantity) || 0;
                return quantity <= 0;
            });
        }

        if (filterBy === 'batch' && batchFilter.trim()) {
            const batchTerm = batchFilter.toLowerCase();
            filtered = filtered.filter(product =>
                product.batch_no?.toLowerCase().includes(batchTerm)
            );
        }

        if (filterBy === 'barcode' && barcodeFilter.trim()) {
            const barcodeTerm = barcodeFilter.toLowerCase();
            filtered = filtered.filter(product =>
                product.barcode?.toLowerCase().includes(barcodeTerm)
            );
        }

        if (filterBy === 'category' && categoryFilter.trim()) {
            filtered = filtered.filter(product =>
                product.category?.toLowerCase() === categoryFilter.toLowerCase()
            );
        }

        if (filterBy === 'company' && companyFilter.trim()) {
            const companyTerm = companyFilter.toLowerCase();
            filtered = filtered.filter(product =>
                product.company?.toLowerCase().includes(companyTerm)
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const addedByTerm = addedByFilter.toLowerCase();
            filtered = filtered.filter(product =>
                (product.added_by_username?.toLowerCase().includes(addedByTerm)) ||
                (product.username?.toLowerCase().includes(addedByTerm))
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            filtered = filtered.filter(product => {
                if (!product.created_at) return false;
                const productDate = new Date(product.created_at);
                return productDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            filtered = filtered.filter(product => {
                if (!product.created_at) return false;
                const productDate = new Date(product.created_at);
                end.setHours(23, 59, 59, 999);
                return productDate <= end;
            });
        }

        setFilteredProducts(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setBatchFilter('');
        setBarcodeFilter('');
        setCategoryFilter('');
        setCompanyFilter('');
        setAddedByFilter('');
        setStockCategoryFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleView = (product) => {
        setSelectedProduct(product);
        setShowViewModal(true);
    };

    const handleEdit = (product) => {
        if (product.username !== user.username) {
            showToast('error', 'You can only edit your own stock records');
            return;
        }

        setSelectedProduct(product);
        setEditFormData({
            product_name: product.product_name || '',
            batch_no: product.batch_no || '',
            barcode: product.barcode || '',
            category: product.category || '',
            company: product.company || '',
            unit: product.unit || '',
            quantity: product.quantity || '',
            quantity_alert: product.quantity_alert || '',
            stock_category: product.stock_category || 'Saleable'
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (productId) => {
        const product = products.find(p => p.id === productId);
        if (!product || product.username !== user.username) {
            showToast('error', 'You can only delete your own stock records');
            return;
        }

        setProductToDelete(productId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!productToDelete) return;

        const product = products.find(p => p.id === productToDelete);
        if (!product || product.username !== user.username) {
            showToast('error', 'You can only delete your own stock records');
            setShowDeleteModal(false);
            setProductToDelete(null);
            return;
        }

        const currentShopId = localStorage.getItem('current_shop_id') || user.shop_id;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: productToDelete,
                    username: user.username,
                    shop_id: currentShopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Stock item deleted successfully');
                fetchProducts();
                setShowDeleteModal(false);
                setProductToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting stock item');
            }
        } catch (error) {
            console.error('Error deleting product:', error);
            showToast('error', translations.deleteError || 'Error deleting stock item');
        }
    };

    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.product_name.trim()) {
            newErrors.product_name = translations.nameRequired || 'Product name is required';
        }

        if (!editFormData.batch_no.trim()) {
            newErrors.batch_no = translations.batchRequired || 'Batch number is required';
        }

        if (!editFormData.unit) {
            newErrors.unit = translations.unitRequired || 'Unit is required';
        }

        if (!editFormData.quantity || parseFloat(editFormData.quantity) < 0) {
            newErrors.quantity = translations.quantityRequired || 'Quantity is required';
        }

        if (!editFormData.quantity_alert || parseFloat(editFormData.quantity_alert) < 0) {
            newErrors.quantity_alert = translations.alertRequired || 'Low stock alert quantity is required';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateProduct = async (e) => {
        e.preventDefault();

        if (!selectedProduct) return;

        if (selectedProduct.username !== user.username) {
            showToast('error', 'You can only update your own stock records');
            return;
        }

        const currentShopId = localStorage.getItem('current_shop_id') || user.shop_id;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedProduct.id,
                    ...editFormData,
                    username: user.username,
                    shop_id: currentShopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.stockUpdated || 'Stock updated successfully');
                fetchProducts();
                setShowEditModal(false);
                setSelectedProduct(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating stock');
            }
        } catch (error) {
            console.error('Error updating product:', error);
            showToast('error', translations.updateError || 'Error updating stock');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getLogoForPDF = () => {
        if (!businessInfo.bussinesslogo) return '';

        try {
            if (businessInfo.bussinesslogo.startsWith('data:image')) {
                return businessInfo.bussinesslogo;
            }

            if (businessInfo.bussinesslogo.startsWith('http')) {
                return businessInfo.bussinesslogo;
            }

            if (businessInfo.bussinesslogo.length > 100) {
                return `data:image/png;base64,${businessInfo.bussinesslogo}`;
            }

            return '';
        } catch (error) {
            console.error('Error processing logo:', error);
            return '';
        }
    };

    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    const currentDateGlobal = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const currentTimeGlobal = () => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const handleExportPDF = async () => {
        if (filteredProducts.length === 0) {
            showToast('error', translations.noStockFound || 'No data to export!');
            return;
        }

        setPdfLoading(true);

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#10b981';

            const pageSizeMap = {
                'a4': 'a4',
                'letter': 'letter',
                'legal': 'legal',
                'a5': 'a5'
            };

            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';

            const pdf = new jsPDF({
                orientation: pdfOrientation,
                unit: 'mm',
                format: pageFormat,
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const rtlTextStyles = isRTL ? `
                unicode-bidi: plaintext;
                text-rendering: optimizeLegibility;
                font-kerning: normal;
                font-variant-ligatures: common-ligatures;
                font-feature-settings: "calt", "liga", "kern";
                letter-spacing: 0;
                word-spacing: normal;
            ` : '';

            const headerCellStyle = `
                padding: 10px 8px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: ${pdfOrientation === 'portrait' ? '9px' : '8px'}; 
                letter-spacing: ${isRTL ? '0' : '0.3px'};
                border-right: 1px solid rgba(255,255,255,0.1);
                ${rtlTextStyles}
            `;

            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');

                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                const r = parseInt(themeColor[0]);
                const g = parseInt(themeColor[1]);
                const b = parseInt(themeColor[2]);

                pdfDoc.setFillColor(r, g, b);
                pdfDoc.setDrawColor(r, g, b);
                pdfDoc.setLineWidth(0.2);

                const dotSize = 0.3;
                const dotSpacing = 8;

                for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                    for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                        pdfDoc.circle(x, y, dotSize, 'F');
                    }
                }

                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            const rowsPerPage = getRowsPerPage();

            let productsToExport = [...filteredProducts];

            if (pdfPageRange === 'range') {
                const totalPagesCount = Math.ceil(productsToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPagesCount, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, productsToExport.length);
                productsToExport = productsToExport.slice(startIdx, endIdx);
            }

            const totalPagesCount = Math.ceil(productsToExport.length / rowsPerPage);

            const filterInfoHTML = (searchTerm || filterBy !== 'all' || startDate || endDate || filterType !== 'all' || stockCategoryFilter) ? `
                <div style="margin: 8px 0; padding: 6px; background: #f9fafb; border-radius: 6px; font-size: 8px; color: #6b7280; text-align: center;">
                    ${translations.reportFilters || 'Applied Filters'}: 
                    ${searchTerm ? `Search: ${searchTerm}` : ''}
                    ${stockCategoryFilter ? ` | Type: ${stockCategoryFilter}` : ''}
                    ${batchFilter ? ` | Batch: ${batchFilter}` : ''}
                    ${barcodeFilter ? ` | Barcode: ${barcodeFilter}` : ''}
                    ${categoryFilter ? ` | Category: ${categoryFilter}` : ''}
                    ${companyFilter ? ` | Company: ${companyFilter}` : ''}
                    ${startDate ? ` | From: ${startDate}` : ''}
                    ${endDate ? ` | To: ${endDate}` : ''}
                    ${filterType !== 'all' ? ` | Stock: ${filterType === 'lowStock' ? 'Low Stock' : 'Out of Stock'}` : ''}
                </div>
            ` : '';

            const summaryStatsHTML = `
                <div style="display: flex; justify-content: center; gap: 20px; margin: 10px 0; flex-wrap: wrap;">
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.allStock || 'Total Items'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: ${primaryColor};">${productsToExport.length}</div>
                    </div>
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalValue || 'Total Quantity'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: #059669;">${productsToExport.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0).toFixed(2)}</div>
                    </div>
                    <div style="background: #fef3c7; padding: 5px 15px; border-radius: 20px; border: 1px solid rgba(245, 158, 11, 0.2);">
                        <div style="font-size: 10px; color: #92400e; ${rtlTextStyles}">${translations.lowStockFilter || 'Low Stock'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: #d97706;">${productsToExport.filter(p => { const qty = parseFloat(p.quantity) || 0; const alert = parseFloat(p.quantity_alert) || 0; return qty <= alert && qty > 0; }).length}</div>
                    </div>
                    <div style="background: #fee2e2; padding: 5px 15px; border-radius: 20px; border: 1px solid rgba(239, 68, 68, 0.2);">
                        <div style="font-size: 10px; color: #991b1b; ${rtlTextStyles}">${translations.outOfStockFilter || 'Out of Stock'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: #dc2626;">${productsToExport.filter(p => { const qty = parseFloat(p.quantity) || 0; return qty <= 0; }).length}</div>
                    </div>
                </div>
            `;

            const headerHTML = `
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${hexToRgba(primaryColor, 0.05)}">
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 22px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'}; ${rtlTextStyles}">
                            ${businessInfo.businessname || (translations.stockList || 'Stock List')}
                        </h1>
                        <div style="color: #666; font-size: 11px; font-weight: 500; margin-top: 3px; ${rtlTextStyles}">
                            ${translations.inventoryReport || 'Inventory Report'} - ${currentDateGlobal()} ${currentTimeGlobal()}
                            ${currentShop ? ` | Shop: ${currentShop.name}` : ''}
                        </div>
                        ${businessInfo.businessaddress ? `<div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">${businessInfo.businessaddress}</div>` : ''}
                        <div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">
                            Generated by: ${user?.fullname || user?.username || 'System'}
                        </div>
                    </div>
                    ${summaryStatsHTML}
                </div>
            `;

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: ${pdfOrientation === 'portrait' ? '8px' : '7px'}; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle}">${translations.serialNo || 'No.'}</th>
                            <th style="${headerCellStyle}">${translations.updatedAt || 'Updated'}</th>
                            <th style="${headerCellStyle}">${translations.productName || 'Product'}</th>
                            <th style="${headerCellStyle}">${translations.batchNumber || 'Batch'}</th>
                            <th style="${headerCellStyle}">${translations.category || 'Category'}</th>
                            <th style="${headerCellStyle}">${translations.stockCategory || 'Type'}</th>
                            <th style="${headerCellStyle}">${translations.company || 'Company'}</th>
                            <th style="${headerCellStyle}">${translations.unit || 'Unit'}</th>
                            <th style="${headerCellStyle.replace('border-right:', 'border: none;')}">${translations.quantity || 'Qty'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPagesCount; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, productsToExport.length);
                const pageProducts = productsToExport.slice(startRow, endRow);

                const tableRowsHTML = pageProducts.map((product, index) => {
                    const quantity = parseFloat(product.quantity) || 0;
                    const stockCategory = product.stock_category || 'Saleable';
                    const isRaw = stockCategory === 'Raw';
                    
                    let stockStyle = '';
                    if (quantity <= 0) {
                        stockStyle = 'background: #fee2e2; color: #dc2626;';
                    } else if (quantity <= (parseFloat(product.quantity_alert) || 0)) {
                        stockStyle = 'background: #fef3c7; color: #d97706;';
                    } else {
                        stockStyle = 'background: #d1fae5; color: #059669;';
                    }
                    
                    const typeStyle = isRaw 
                        ? 'background: #fef3c7; color: #d97706; border: 1px solid #fcd34d;' 
                        : 'background: #d1fae5; color: #059669; border: 1px solid #6ee7b7;';
                    
                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; background: ${hexToRgba(primaryColor, 0.1)}; border-radius: 3px; font-size: 9px;">
                                    ${startRow + index + 1}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-flex; align-items: center; gap: 4px; font-family: monospace; font-size: 7px;">
                                    <FontAwesomeIcon icon={faClock} style="font-size: 8px; color: #6b7280;" />
                                    ${product.updated_at ? formatDateTime(product.updated_at) : (product.created_at ? formatDateTime(product.created_at) : '-')}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 500; color: #111827; border-bottom: 1px solid #f0f0f0; font-size: 8px; ${rtlTextStyles}">
                                <div style="font-weight: 600;">${product.product_name || '-'}</div>
                                <div style="font-size: 6px; color: #9ca3af;">ID: ${product.id}</div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 500; color: #374151; border-bottom: 1px solid #f0f0f0; font-size: 8px; font-family: monospace;">
                                ${product.batch_no || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-block; padding: 2px 8px; background: #f3e8ff; border-radius: 12px; color: #7c3aed;">
                                    ${translations[product.category] || product.category || '-'}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-block; padding: 2px 8px; border-radius: 12px; ${typeStyle} font-weight: 500;">
                                    ${stockCategory === 'Saleable' ? (translations.saleable || 'Saleable') : (translations.raw || 'Raw Material')}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px; ${rtlTextStyles}">
                                ${product.company || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #059669; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${translations[product.unit] || product.unit || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-block; padding: 2px 10px; border-radius: 12px; ${stockStyle}">
                                    ${quantity.toFixed(2)}
                                </div>
                            </td>
                        </tr>
                    `;
                }).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';

                const pageHTML = `
                    <div style="width: 800px; padding: 15px 20px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        ${filterInfoHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '800px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    onclone: (clonedDoc) => {
                        if (isRTL) {
                            const elements = clonedDoc.querySelectorAll('div, span, td, th, h1');
                            elements.forEach(el => {
                                el.style.unicodeBidi = 'plaintext';
                                el.style.textRendering = 'optimizeLegibility';
                                el.style.fontFeatureSettings = '"calt", "liga", "kern"';
                            });
                        }
                    }
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                addPatternBackground(pdf);
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPagesCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }

            const pageSizeText = pdfPageSize === 'a4' ? 'A4' : pdfPageSize === 'letter' ? 'Letter' : pdfPageSize === 'legal' ? 'Legal' : 'A5';
            const orientationText = pdfOrientation === 'portrait' ? 'Portrait' : 'Landscape';
            const fileName = `stock_${currentShop?.name?.replace(/[^a-z0-9]/gi, '_') || 'report'}_${pageSizeText}_${orientationText}_${currentDateGlobal()}.pdf`;

            pdf.save(fileName);

            showToast('success', `PDF exported successfully with ${productsToExport.length} stock records across ${totalPagesCount} pages!`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    const formatQuantity = (quantity) => {
        if (!quantity && quantity !== 0) return '-';
        return parseFloat(quantity).toFixed(2);
    };

    const getStockStatus = (quantity, alert) => {
        const qty = parseFloat(quantity) || 0;
        const alertQty = parseFloat(alert) || 0;
        
        if (qty <= 0) {
            return { status: 'out-of-stock', text: translations.outOfStock || 'Out of Stock', color: '#ef4444' };
        } else if (qty <= alertQty) {
            return { status: 'low-stock', text: translations.lowStock || 'Low Stock', color: '#f59e0b' };
        } else {
            return { status: 'in-stock', text: translations.inStock || 'In Stock', color: '#10b981' };
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faBoxes} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.stockList || 'Stock List'}</h1>
                        <p>
                            {user ? `${translations.allStock || 'My Stock'} - ${user.username}` : translations.allStock || 'My Stock'}
                            {currentShop && <span style={{ marginLeft: '10px', color: 'var(--primary-color)' }}> | {currentShop.name}</span>}
                        </p>
                    </div>
                </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={stockCategoryFilter}
                            onChange={(e) => setStockCategoryFilter(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            {stockCategoryOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="batch">{translations.batchNumberFilter || 'Batch Number'}</option>
                            <option value="barcode">{translations.barcodeFilter || 'Barcode'}</option>
                            <option value="category">{translations.categoryFilter || 'Category'}</option>
                            <option value="company">{translations.companyFilter || 'Company'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'batch' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={batchFilter}
                                onChange={(e) => setBatchFilter(e.target.value)}
                                placeholder={translations.batchNumberFilter || 'Batch number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'barcode' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={barcodeFilter}
                                onChange={(e) => setBarcodeFilter(e.target.value)}
                                placeholder={translations.barcodeFilter || 'Barcode...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'category' && (
                        <div className="filter-group compact">
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                <option value="">{translations.selectCategory || 'Select Category'}</option>
                                {categories.filter(cat => cat.value).map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'company' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                                placeholder={translations.companyFilter || 'Company...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.stockStatus || 'Stock Status'}</option>
                            <option value="lowStock">{translations.lowStockFilter || 'Low Stock'}</option>
                            <option value="outOfStock">{translations.outOfStockFilter || 'Out of Stock'}</option>
                        </select>
                    </div>
                    
                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search stock...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchProducts}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={pdfOrientation}
                            onChange={(e) => setPdfOrientation(e.target.value)}
                            className="filter-select compact"
                            title={translations.orientation || 'Orientation'}
                            style={{ minWidth: '100px' }}
                        >
                            <option value="portrait">{translations.portrait || 'Portrait'}</option>
                            <option value="landscape">{translations.landscape || 'Landscape'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={pdfPageSize}
                            onChange={(e) => setPdfPageSize(e.target.value)}
                            className="filter-select compact"
                            title={translations.pageSize || 'Page Size'}
                            style={{ minWidth: '90px' }}
                        >
                            <option value="a4">{translations.a4 || 'A4'}</option>
                            <option value="letter">{translations.letter || 'Letter'}</option>
                            <option value="legal">{translations.legal || 'Legal'}</option>
                            <option value="a5">{translations.a5 || 'A5'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={pdfPageRange}
                            onChange={(e) => setPdfPageRange(e.target.value)}
                            className="filter-select compact"
                            title="Page Range"
                            style={{ minWidth: '110px' }}
                        >
                            <option value="all">All Pages</option>
                            <option value="range">Page Range</option>
                        </select>
                    </div>

                    {pdfPageRange === 'range' && (
                        <>
                            <div className="filter-group compact" style={{ width: '70px' }}>
                                <input
                                    type="number"
                                    min="1"
                                    value={pdfStartPage}
                                    onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="filter-input compact"
                                    placeholder="From"
                                    style={{ textAlign: 'center' }}
                                />
                            </div>
                            <div className="filter-group compact" style={{ width: '70px' }}>
                                <input
                                    type="number"
                                    min={pdfStartPage}
                                    value={pdfEndPage}
                                    onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))}
                                    className="filter-input compact"
                                    placeholder="To"
                                    style={{ textAlign: 'center' }}
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact">
                        <button
                            onClick={handleExportPDF}
                            className="refresh-btn-modern compact"
                            disabled={loading || filteredProducts.length === 0 || pdfLoading}
                            title={translations.exportToPDF || 'Export PDF'}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                        >
                            {pdfLoading ? (
                                <div className="btn-spinner-small"></div>
                            ) : (
                                <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            <span className="btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading your stock data...'}</p>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.updatedAt || 'Updated'}</th>
                                            <th className="name-column centered-cell">{translations.productName || 'Product Name'}</th>
                                            <th className="centered-cell">{translations.batchNumber || 'Batch'}</th>
                                            <th className="centered-cell">{translations.category || 'Category'}</th>
                                            <th className="centered-cell">{translations.stockCategory || 'Type'}</th>
                                            <th className="centered-cell">{translations.company || 'Company'}</th>
                                            <th className="centered-cell">{translations.unit || 'Unit'}</th>
                                            <th className="centered-cell">{translations.quantity || 'Qty'}</th>
                                            <th className="centered-cell">{translations.stockStatus || 'Status'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            {/* <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentProducts.map((product, index) => {
                                            const stockStatus = getStockStatus(product.quantity, product.quantity_alert);
                                            const addedBy = product.added_by_username || product.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            const isRawProduct = product.stock_category === 'Raw';
                                            
                                            const rowStyle = isRawProduct ? {
                                                backgroundColor: 'rgba(245, 158, 11, 0.08)',
                                                borderLeft: '3px solid #f59e0b'
                                            } : {
                                                backgroundColor: 'rgba(16, 185, 129, 0.04)',
                                                borderLeft: '3px solid #10b981'
                                            };
                                            
                                            return (
                                                <tr key={product.id} style={rowStyle}>
                                                    <td className="id-cell centered-cell">
                                                        <span className="serial-number">{startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontSize: '11px', fontFamily: 'monospace' }}>
                                                            <FontAwesomeIcon icon={faClock} size="xs" style={{ color: '#6b7280' }} />
                                                            <span>{formatDateTime(product.updated_at || product.created_at)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="name-cell centered-cell">
                                                        <div className="product-name-cell">
                                                            <span className="product-name-text">{product.product_name}</span>
                                                        </div>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="batch-text">{product.batch_no}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="category-badge">
                                                            {translations[product.category] || product.category || '-'}
                                                        </span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <div style={{
                                                            display: 'inline-block',
                                                            padding: '4px 10px',
                                                            borderRadius: '20px',
                                                            fontSize: '11px',
                                                            fontWeight: '500',
                                                            backgroundColor: isRawProduct ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                                            color: isRawProduct ? '#d97706' : '#059669',
                                                            border: isRawProduct ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)'
                                                        }}>
                                                            {isRawProduct ? (translations.raw || 'Raw Material') : (translations.saleable || 'Saleable')}
                                                        </div>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="company-text">{product.company || '-'}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className="unit-text">{translations[product.unit] || product.unit}</span>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <div className="quantity-display">
                                                            <span className={`quantity-text ${parseFloat(product.quantity) <= parseFloat(product.quantity_alert) ? 'low-stock-qty' : ''}`}>
                                                                {parseFloat(product.quantity || 0).toFixed(2)}
                                                            </span>
                                                            {parseFloat(product.quantity) <= parseFloat(product.quantity_alert) && parseFloat(product.quantity) > 0 && (
                                                                <span className="alert-icon" title="Low Stock" style={{ marginLeft: '5px', color: '#f59e0b' }}>
                                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                </span>
                                                            )}
                                                        </div>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <div 
                                                            className="stock-status-badge"
                                                            style={{ 
                                                                backgroundColor: stockStatus.color + '20',
                                                                color: stockStatus.color,
                                                                border: `1px solid ${stockStatus.color}40`,
                                                                display: 'inline-block',
                                                                padding: '4px 10px',
                                                                borderRadius: '20px',
                                                                fontSize: '11px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            {stockStatus.text}
                                                        </div>
                                                     </td>
                                                    <td className="centered-cell">
                                                        <span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`} style={{
                                                            display: 'inline-block',
                                                            padding: '4px 10px',
                                                            borderRadius: '20px',
                                                            fontSize: '11px',
                                                            fontWeight: '500',
                                                            backgroundColor: '#e0e7ff',
                                                            color: '#4f46e5'
                                                        }}>
                                                            <FontAwesomeIcon icon={faUser} className="user-icon" size="xs" style={{ marginRight: '5px' }} />
                                                            <span>{addedBy}</span>
                                                        </span>
                                                     </td>
                                                    {/* <td className="actions-cell-modern centered-cell">
                                                        <div className="actions-content">
                                                            <button
                                                                onClick={() => handleView(product)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    margin: '0 5px',
                                                                    color: '#3b82f6'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(product)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                                disabled={product.username !== user?.username}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: product.username !== user?.username ? 'not-allowed' : 'pointer',
                                                                    margin: '0 5px',
                                                                    color: '#f59e0b',
                                                                    opacity: product.username !== user?.username ? 0.5 : 1
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(product.id)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                                disabled={product.username !== user?.username}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: product.username !== user?.username ? 'not-allowed' : 'pointer',
                                                                    margin: '0 5px',
                                                                    color: '#ef4444',
                                                                    opacity: product.username !== user?.username ? 0.5 : 1
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                     </td> */}
                                                 </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredProducts.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedProduct && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faBox} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.stockDetails || 'Stock Details'}</h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.productId || 'Product ID'}:</span>
                                <span className="detail-value">#{selectedProduct.id}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.updatedAt || 'Last Updated'}:</span>
                                <span className="detail-value">{formatDateTime(selectedProduct.updated_at || selectedProduct.created_at)}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.productName || 'Product Name'}:</span>
                                <span className="detail-value">{selectedProduct.product_name}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.batchNumber || 'Batch Number'}:</span>
                                <span className="detail-value">{selectedProduct.batch_no}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.barcode || 'Barcode'}:</span>
                                <span className="detail-value" style={{ fontFamily: 'monospace' }}>{selectedProduct.barcode}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.stockCategory || 'Stock Category'}:</span>
                                <span className="detail-value">
                                    <div style={{
                                        display: 'inline-block',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        backgroundColor: selectedProduct.stock_category === 'Raw' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                        color: selectedProduct.stock_category === 'Raw' ? '#d97706' : '#059669',
                                        border: selectedProduct.stock_category === 'Raw' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)'
                                    }}>
                                        <FontAwesomeIcon icon={selectedProduct.stock_category === 'Raw' ? faIndustry : faCheckCircle} style={{ marginRight: '5px' }} />
                                        <span>{selectedProduct.stock_category === 'Raw' ? (translations.raw || 'Raw Material') : (translations.saleable || 'Saleable')}</span>
                                    </div>
                                </span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.category || 'Category'}:</span>
                                <span className="detail-value">{selectedProduct.category || translations.notProvided || 'Not provided'}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.company || 'Company'}:</span>
                                <span className="detail-value">{selectedProduct.company || translations.notProvided || 'Not provided'}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.unit || 'Unit'}:</span>
                                <span className="detail-value">{selectedProduct.unit || translations.notProvided || 'Not provided'}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.quantity || 'Quantity'}:</span>
                                <span className="detail-value">{parseFloat(selectedProduct.quantity || 0).toFixed(2)}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.quantityAlert || 'Low Stock Alert'}:</span>
                                <span className="detail-value">{parseFloat(selectedProduct.quantity_alert || 0).toFixed(2)}</span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.stockStatus || 'Stock Status'}:</span>
                                <span className="detail-value">
                                    <div className="stock-status-badge" style={{ 
                                        backgroundColor: getStockStatus(selectedProduct.quantity, selectedProduct.quantity_alert).color + '20',
                                        color: getStockStatus(selectedProduct.quantity, selectedProduct.quantity_alert).color,
                                        border: `1px solid ${getStockStatus(selectedProduct.quantity, selectedProduct.quantity_alert).color}40`,
                                        display: 'inline-block',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '500'
                                    }}>
                                        {getStockStatus(selectedProduct.quantity, selectedProduct.quantity_alert).text}
                                    </div>
                                </span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.addedBy || 'Added By'}:</span>
                                <span className="detail-value">
                                    <span className="added-by-badge" style={{ background: '#e0e7ff', color: '#4f46e5', display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>
                                        <FontAwesomeIcon icon={faUser} size="xs" style={{ marginRight: '5px' }} />
                                        {selectedProduct.added_by_username || selectedProduct.username || '-'}
                                    </span>
                                </span>
                            </div>
                            <div className="detail-row-modern">
                                <span className="detail-label">{translations.date || 'Date Added'}:</span>
                                <span className="detail-value">{formatDate(selectedProduct.date)}</span>
                            </div>
                            {selectedProduct.shop_name && (
                                <div className="detail-row-modern">
                                    <span className="detail-label">{translations.shopInfo || 'Shop'}:</span>
                                    <span className="detail-value">{selectedProduct.shop_name} {selectedProduct.shop_code ? `(${selectedProduct.shop_code})` : ''}</span>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE */}
            {showEditModal && selectedProduct && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div 
                        ref={modalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDrag} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faEdit} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.editStock || 'Edit Stock'}</h3>
                                <span className="product-number-badge">#{selectedProduct.id}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setWidthMode(key)}
                                        title={translations[mode.label] || mode.label}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)} type="button">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateProduct} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                                <div className="form-grid edit-form-grid">
                                    {/* Product Name Field */}
                                    <div className={`form-group ${editFormErrors.product_name ? 'has-error' : ''}`}>
                                        <label htmlFor="product_name">{translations.productName || 'Product Name'} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="product_name"
                                            name="product_name"
                                            value={editFormData.product_name}
                                            onChange={handleEditFormChange}
                                            required
                                            className={editFormErrors.product_name ? 'error' : ''}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {editFormErrors.product_name && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.product_name}
                                            </div>
                                        )}
                                    </div>

                                    {/* Batch Number Field (Read-only in edit mode) */}
                                    <div className={`form-group ${editFormErrors.batch_no ? 'has-error' : ''}`}>
                                        <label htmlFor="batch_no">{translations.batchNumber || 'Batch Number'} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="batch_no"
                                            name="batch_no"
                                            value={editFormData.batch_no}
                                            onChange={handleEditFormChange}
                                            required
                                            readOnly
                                            disabled
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {editFormErrors.batch_no && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.batch_no}
                                            </div>
                                        )}
                                        <div className="readonly-notice">
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                            {translations.readonlyNotice || 'Batch number cannot be edited after creation'}
                                        </div>
                                    </div>

                                    {/* Barcode Field (Read-only) */}
                                    <div className="form-group">
                                        <label htmlFor="barcode">{translations.barcode || 'Barcode'}</label>
                                        <input
                                            type="text"
                                            id="barcode"
                                            name="barcode"
                                            value={editFormData.barcode}
                                            readOnly
                                            disabled
                                            className="readonly-input"
                                            dir="ltr"
                                        />
                                    </div>

                                    {/* Stock Category Field */}
                                    <div className="form-group">
                                        <label htmlFor="stock_category">{translations.stockCategory || 'Stock Category'}</label>
                                        <select
                                            id="stock_category"
                                            name="stock_category"
                                            value={editFormData.stock_category}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <option value="Saleable">{translations.saleable || 'Saleable'}</option>
                                            <option value="Raw">{translations.raw || 'Raw Material'}</option>
                                        </select>
                                    </div>

                                    {/* Category Field */}
                                    <div className="form-group">
                                        <label htmlFor="category">{translations.category || 'Category'}</label>
                                        <select
                                            id="category"
                                            name="category"
                                            value={editFormData.category}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {categories.map((cat) => (
                                                <option key={cat.value} value={cat.value}>
                                                    {translations[cat.label.toLowerCase()] || cat.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Company/Brand Field */}
                                    <div className="form-group">
                                        <label htmlFor="company">{translations.company || 'Company / Brand'}</label>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            value={editFormData.company}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    {/* Unit Field */}
                                    <div className={`form-group ${editFormErrors.unit ? 'has-error' : ''}`}>
                                        <label htmlFor="unit">{translations.unit || 'Unit'} <span className="required">*</span></label>
                                        <select
                                            id="unit"
                                            name="unit"
                                            value={editFormData.unit}
                                            onChange={handleEditFormChange}
                                            required
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>
                                                    {translations[unit.label.toLowerCase()] || unit.label}
                                                </option>
                                            ))}
                                        </select>
                                        {editFormErrors.unit && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.unit}
                                            </div>
                                        )}
                                    </div>

                                    {/* Quantity Field */}
                                    <div className={`form-group ${editFormErrors.quantity ? 'has-error' : ''}`}>
                                        <label htmlFor="quantity">{translations.quantity || 'Quantity'} <span className="required">*</span></label>
                                        <input
                                            type="number"
                                            id="quantity"
                                            name="quantity"
                                            value={editFormData.quantity}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.001"
                                            min="0"
                                            className={editFormErrors.quantity ? 'error' : ''}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        {editFormErrors.quantity && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.quantity}
                                            </div>
                                        )}
                                    </div>

                                    {/* Low Stock Alert Field */}
                                    <div className={`form-group ${editFormErrors.quantity_alert ? 'has-error' : ''}`}>
                                        <label htmlFor="quantity_alert">{translations.quantityAlert || 'Low Stock Alert'} <span className="required">*</span></label>
                                        <input
                                            type="number"
                                            id="quantity_alert"
                                            name="quantity_alert"
                                            value={editFormData.quantity_alert}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.001"
                                            min="0"
                                            className={editFormErrors.quantity_alert ? 'error' : ''}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        {editFormErrors.quantity_alert && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.quantity_alert}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Product Info Footer */}
                                <div className="edit-product-info">
                                    <p><strong>{translations.productId || 'Product ID'}:</strong> #{selectedProduct.id}</p>
                                    <p><strong>{translations.batchNumber || 'Batch'}:</strong> {selectedProduct.batch_no}</p>
                                    <p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedProduct.added_by_username || selectedProduct.username}</p>
                                </div>
                            </div>

                            <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)} disabled={editLoading}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        {/* Loading Overlay for Edit */}
                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                    <p className="loading-subtext">Please wait while we update stock data</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this stock item?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockList;